import { format } from 'date-fns';
import { useMemo } from 'react';
import { SystemEntityInline } from '@/components/system-entities/inline-ui';
import { SystemEntity } from '@erp/shared-types';
import { Separator } from '@/components/ui/separator';
import { usePaymentAssociations } from '@/api/rpc/hooks/core/useEntityAssociationsFiltered';
import type { Payment } from '@erp/rpc';

export interface PaymentDetailsProps {
  payment: Payment;
}

export function PaymentDetails({ payment }: PaymentDetailsProps) {
  const { associations } = usePaymentAssociations(payment.id);

  // Find remittance email association
  const remittanceEmailId = useMemo(() => {
    const remittanceAssociation = associations.find(
      (assoc) => assoc.entity.entityType === SystemEntity.OUTBOUND_EMAIL,
    );
    return remittanceAssociation?.entity.entityId ?? null;
  }, [associations]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
        {/* Amount Section */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div>
            <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Amount
            </h3>
            <p className="text-3xl font-bold">
              {Number(payment.amount).toLocaleString(undefined, {
                style: 'currency',
                currency: payment.currency || 'USD',
              })}
            </p>
          </div>
        </div>

        <Separator />

        {/* Payment Details Grid */}
        <div className="space-y-4">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Payment Details
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Payment Date</p>
              <p className="text-sm font-medium">
                {format(new Date(payment.paymentDate), 'MMMM do, yyyy')}
              </p>
            </div>

            <div>
              <p className="text-xs text-muted-foreground mb-1">Method</p>
              <p className="text-sm font-medium uppercase">{payment.method}</p>
            </div>

            {payment.referenceNumber && (
              <div className="col-span-2">
                <p className="text-xs text-muted-foreground mb-1">
                  Reference Number
                </p>
                <p className="text-sm font-medium font-mono">
                  {payment.referenceNumber}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Memo Section */}
        {payment.memo && (
          <>
            <Separator />
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Memo
              </h3>
              <p className="text-sm leading-relaxed">{payment.memo}</p>
            </div>
          </>
        )}

        {/* Remittance Information */}
        {remittanceEmailId && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Remittance Information
              </h3>
              <div>
                <SystemEntityInline
                  entityType={SystemEntity.OUTBOUND_EMAIL}
                  entityId={remittanceEmailId}
                />
              </div>
            </div>
          </>
        )}

        {/* Associated Invoices */}
        {(payment.invoices?.length ?? 0) > 0 && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Invoices ({payment.invoices?.length})
              </h3>
              <div className="flex flex-col gap-2">
                {payment.invoices?.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between"
                  >
                    <SystemEntityInline
                      entityType={SystemEntity.AP_INVOICE}
                      entityId={inv.id}
                    />
                    <span className="text-sm font-mono text-muted-foreground">
                      {Number(inv.amount).toLocaleString(undefined, {
                        style: 'currency',
                        currency: payment.currency || 'USD',
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Legacy single invoice fallback */}
        {payment.invoice && !(payment.invoices?.length ?? 0) && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Invoice
              </h3>
              <SystemEntityInline
                entityType={SystemEntity.AP_INVOICE}
                entityId={payment.invoice.id}
              />
            </div>
          </>
        )}

        {/* Financial Transaction */}
        {payment.financialTransaction && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Financial Transaction
              </h3>
              <SystemEntityInline
                entityType={SystemEntity.FINANCIAL_TRANSACTION}
                entityId={payment.financialTransaction.id}
              />
            </div>
          </>
        )}
      </div>

      {/* Footer with Timestamps */}
      <div className="border-t px-6 py-3 bg-muted/20">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div>
            <span className="font-medium">Created:</span>{' '}
            {format(new Date(payment.createdAt), 'MMM d, yyyy, h:mm a')}
          </div>
          <div>
            <span className="font-medium">Updated:</span>{' '}
            {format(new Date(payment.updatedAt), 'MMM d, yyyy, h:mm a')}
          </div>
        </div>
      </div>
    </div>
  );
}
